import { Box, CircularProgress } from '@material-ui/core';
import { IntegrationType, SignatureUrls } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { useLoanPaymentIntent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/hooks/useLoanPaymentIntent';
import { SignatureCompleted } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/NextActions/components/SignContract/Signature/SamlSignature/components/SignatureCompleted';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';

const SAML_ID = 'formSAML';
const SMAL_TIMEOUT = 30000; // max time to wait for the form to appear

interface SamlSignatureProps {
  signatureUrls: SignatureUrls;
  paymentMethodDetailEntity: LoanPaymentMethodDetailEntity;
  openDialogOnRender?: boolean;
  disabled?: boolean;
}

export const SamlSignature = ({
  signatureUrls,
  paymentMethodDetailEntity,
  openDialogOnRender,
  disabled,
}: SamlSignatureProps) => {
  const { md } = useScreen();
  const { palette } = useCustomTheme();
  const { updatePaymentMethodEntity } = useLoanPaymentIntent();

  const [showDialog, setShowDialog] = useState(openDialogOnRender ?? false);
  const [signatureCompleted, setSignatureCompleted] = useState(false);
  const [loading, setLoading] = useState(false);

  const { saml, url, integrationType } = signatureUrls;

  useEffect(() => {
    if (showDialog === false) {
      setSignatureCompleted(false);
    }
  }, [showDialog]);

  useEffect(() => {
    window.addEventListener('message', onCallBackEvent);
    return () => {
      window.removeEventListener('message', onCallBackEvent);
    };
  }, []);

  const onCallBackEvent = async (e: MessageEvent) => {
    if (e.data.msg === 'callback:end') {
      setSignatureCompleted(true);
      setLoading(true);
      try {
        await paymentMethodDetailEntity.waitUntilSigned();
        updatePaymentMethodEntity(paymentMethodDetailEntity);
      } catch {}
      setLoading(false);
    }
  };

  if (!saml || !url || integrationType !== IntegrationType.EMBEDDED_SAML) {
    return null;
  }

  const onMount = async () => {
    setShowDialog(true);
    setLoading(true);
    try {
      await waitForFormAndSubmit();
    } catch (err) {
      // Handle the error, e.g., show a message to the user
      console.error(err);
    }
    setLoading(false);
  };

  const waitForFormAndSubmit = (timeoutMs = SMAL_TIMEOUT): Promise<void> => {
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        const form = document.getElementById(SAML_ID) as HTMLFormElement | null;
        if (form) {
          form.action = url;
          form.submit();
          clearInterval(interval);
          clearTimeout(timeout);
          return resolve();
        }
      }, 50);

      const timeout = setTimeout(() => {
        clearInterval(interval);
        reject(new Error('Form did not appear within timeout'));
      }, timeoutMs);
    });
  };

  return (
    <>
      <Box flexShrink={0}>
        {loading ? (
          <CircularProgress size={18} style={{ color: palette.secondary.main }} />
        ) : (
          <StandardButton main size="small" onClick={onMount} disabled={showDialog || disabled}>
            <Text tId={'SIGN_CONTRACT'} />
          </StandardButton>
        )}
      </Box>
      <NemuruDialog opened={showDialog} setOpened={setShowDialog}>
        {signatureCompleted && <SignatureCompleted />}
        {!signatureCompleted && (
          <form name={SAML_ID} id={SAML_ID} target="iFrameLgt" method="post" action={url}>
            <textarea hidden={true} id="saml_assertion" name="saml_assertion" value={saml} />
            <iframe
              name="iFrameLgt"
              id="iFrameLgt"
              style={{
                height: md ? '700px' : 'calc(100vh - 150px)',
                width: '100%',
                border: 0,
              }}
            />
          </form>
        )}
      </NemuruDialog>
    </>
  );
};
